<script setup lang="ts">
import { computed, reactive, ref, type VNodeRef } from 'vue';
import type {
  ConnectionFormData,
  ConnectionFormDataPerGroup,
  ConnectionFormDataPerPhase,
} from '../../models/Connection';
import type { AddressSelectedPayload } from '../../types/ideal-postcodes';
import { createUniquenessValidator } from '../../utils/output-connections';
import { PHASE } from '../../utils/constants';
import AttentionCircleFilledSVG from '@shared/assets/icon/attention-circle-filled.svg';
import IdealPostcodesAutocomplete from '../IdealPostcodesAutocomplete.vue';
import type { FormItemInstance, FormItemProps, Rule } from 'ant-design-vue/es/form';
import { useDeviceSpecificationStore } from '../../stores/deviceSpecificationStore';
import { useCommissioningDeviceStore } from '../../stores/commissioningDeviceStore';
import type { Terminal } from '../../models/CommissioningDevice';
import { COUNTRY_CODE } from '@/utils/constants';
import { getPhaseColor } from '../../constants/phase-color';

const props = defineProps<{
  groupNumber: number;
  formDataIndex: number;
  formData: ConnectionFormData;
  connectionGroup: ConnectionFormDataPerGroup;
  placeholder: string;
}>();

const emit = defineEmits<{
  (e: 'updateSingleConnectionName', phase: PHASE, value: ConnectionFormDataPerPhase): void;
}>();

const formItemRefs = ref<Partial<Record<PHASE, FormItemInstance | null>>>({});
const setFormItemRef = (phase: PHASE): VNodeRef => {
  return (el) => {
    formItemRefs.value[phase] = el as FormItemInstance | null;
  };
};

const noConnectionPhaseValidationStatus = reactive<Record<string, FormItemProps['validateStatus']>>(
  {
    L1: '',
    L2: '',
    L3: '',
  },
);

const commissioningDeviceStore = useCommissioningDeviceStore();
const detectedConnections = commissioningDeviceStore.outputConnections;
const detectedConnectionsForGroup = computed(() => {
  return {
    L1: detectedConnections[`L1-${props.groupNumber}` as Terminal],
    L2: detectedConnections[`L2-${props.groupNumber}` as Terminal],
    L3: detectedConnections[`L3-${props.groupNumber}` as Terminal],
  };
});

const deviceSpecificationStore = useDeviceSpecificationStore();
const country = computed(() => deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE);
const postcode = computed(() => deviceSpecificationStore.solshareSite?.sitePostcode);
const useIdealPostcodes = computed(() => country.value === COUNTRY_CODE['United Kingdom']);
const isPhaseNoConnection = (phase: PHASE) => {
  return !detectedConnectionsForGroup.value[phase]?.ready;
};

const createConnectionNameModel = (phase: PHASE) =>
  computed({
    get: () => props.connectionGroup[phase]?.name ?? '',
    set: (name: string) => {
      const currentValue = props.connectionGroup[phase] || {};
      emit('updateSingleConnectionName', phase, {
        ...currentValue,
        name,
        externalPropertyId: null,
        externalPropertyIdSource: null,
      });
    },
  });

const connectionNameModels = {
  [PHASE.L1]: createConnectionNameModel(PHASE.L1),
  [PHASE.L2]: createConnectionNameModel(PHASE.L2),
  [PHASE.L3]: createConnectionNameModel(PHASE.L3),
};

const handleIdpcAddressSelected = async (phase: PHASE, { line1, uprn }: AddressSelectedPayload) => {
  const currentValue = props.connectionGroup[phase] || {};
  emit('updateSingleConnectionName', phase, {
    ...currentValue,
    name: line1,
    externalPropertyId: uprn ?? null,
    externalPropertyIdSource: uprn ? 'uprn' : null,
  });
  formItemRefs.value[phase]?.onFieldChange();
};

const resetNoConnectionValidationStatus = () => {
  noConnectionPhaseValidationStatus.L1 = '';
  noConnectionPhaseValidationStatus.L2 = '';
  noConnectionPhaseValidationStatus.L3 = '';
};

const getValidationRulesForPhase = (phase: PHASE): Rule[] => {
  const inputRequired = isPhaseNoConnection(phase)
    ? []
    : [{ required: true, message: 'Connection name is required', trigger: 'blur' as const }];

  return [
    ...inputRequired,
    { validator: createUniquenessValidator(props.formData, props.formDataIndex, phase) },
  ];
};

const getSharedInputAttrs = (phase: PHASE) => ({
  'addon-before': `${phase}-${props.groupNumber}`,
  size: 'large' as const,
  class: [
    'name-input',
    `name-input__${phase}`,
    { 'name-input__no-connection': isPhaseNoConnection(phase) },
    { 'name-input__idpc': useIdealPostcodes },
  ],
  placeholder: props.placeholder,
  'data-tn': `name-input-${phase}-${props.groupNumber}`,
});

defineExpose({
  resetNoConnectionValidationStatus: resetNoConnectionValidationStatus,
});
</script>

<template>
  <a-form-item
    v-for="phase in Object.values(PHASE)"
    :key="`connection-name-${phase}-${groupNumber}`"
    :ref="setFormItemRef(phase)"
    :name="[formDataIndex, 'connectionGroup', phase, 'name']"
    class="form-item__connection-name"
    :rules="getValidationRulesForPhase(phase)"
  >
    <a-row>
      <a-col
        flex="none"
        class="phase-color-container"
        :style="{ backgroundColor: getPhaseColor(country, phase) }"
      ></a-col>
      <a-col flex="auto">
        <template v-if="useIdealPostcodes">
          <IdealPostcodesAutocomplete
            v-model:value="connectionNameModels[phase].value"
            :inputFieldId="`idpc-autocomplete-input-${phase}-${groupNumber}`"
            :projectCountry="country"
            :postcode="postcode"
            v-bind="getSharedInputAttrs(phase)"
            @address-selected="(payload: AddressSelectedPayload) => handleIdpcAddressSelected(phase, payload)"
          >
            <template #suffix v-if="isPhaseNoConnection(phase)">
              <AttentionCircleFilledSVG />
            </template>
          </IdealPostcodesAutocomplete>
        </template>
        <template v-else>
          <a-input
            v-model:value="connectionNameModels[phase].value"
            v-bind="getSharedInputAttrs(phase)"
          >
            <template #suffix v-if="isPhaseNoConnection(phase)">
              <AttentionCircleFilledSVG />
            </template>
          </a-input>
        </template>
      </a-col>
    </a-row>
  </a-form-item>
</template>

<style scoped lang="less">
.form-item__connection-name {
  width: 100%;
  flex-grow: 1;
  margin-bottom: 0;
  padding-right: @content-side-margin;
}

.phase-color-container {
  width: @phase-color-width;
}

.name-input__no-connection {
  color: @color-primary;

  :deep(.ant-input),
  :deep(.ant-input-affix-wrapper) {
    background-color: @color-bg-no-connection;
    color: @color-primary;
    font-style: italic;
    font-size: 1rem;
  }

  :deep(.ant-input-suffix) {
    width: 1.8rem;
    height: 1.8rem;
  }
}

.name-input__idpc {
  :deep(.ant-input),
  :deep(.ant-input-affix-wrapper) {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    border-left: 0;
  }
}
</style>
